import { Component, Inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';

@Component({
  selector: 'app-meeting-summary-dialog',
  templateUrl: './meeting-summary-dialog.component.html',
  styleUrls: ['./meeting-summary-dialog.component.scss']
})
export class MeetingSummaryDialogComponent {
  acceptedItems: any[] = [];
  rejectedItems: any[] = [];
  allResponses: any[] = [];
  
  // For hoster view
  isHoster: boolean = false;
  meetingTitle: string = '';
  agendaItems: any[] = [];
  allAgendaAnswers: Array<{ agendaIndex: number; agendaItem: any; answers: Array<{ userId: string; userName: string; action: string; comment: string }> }> = [];
  currentAgendaIndex: number = 0;

  constructor(
    private dialogRef: MatDialogRef<MeetingSummaryDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    this.isHoster = data?.isHoster || false;
    
    if (this.isHoster) {
      // Hoster view: Show all members' responses per agenda
      this.meetingTitle = data?.meetingTitle || '';
      this.agendaItems = data?.agendaItems || [];
      this.allAgendaAnswers = data?.allAgendaAnswers || [];
      this.currentAgendaIndex = 0;
    } else {
      // Member view: Show their own responses
      this.acceptedItems = data?.acceptedItems || [];
      this.rejectedItems = data?.rejectedItems || [];
      const deferredItems = data?.deferredItems || [];
      
      // Combine all items with their status - use submittedDate if available, otherwise use dateTime
      this.allResponses = [
        ...this.acceptedItems,
        ...this.rejectedItems,
        ...deferredItems
      ];
    }
  }

  getAcceptedCount(): number {
    return this.acceptedItems.length;
  }

  getRejectedCount(): number {
    return this.rejectedItems.length;
  }

  getDeferredCount(): number {
    if (this.isHoster) {
      return 0; // Not used in hoster view
    }
    return this.allResponses.filter(r => r.response === 'Deferred').length;
  }

  // Get current agenda item answers
  get currentAgendaAnswers(): Array<{ userId: string; userName: string; action: string; comment: string }> {
    const currentAgenda = this.allAgendaAnswers.find(a => a.agendaIndex === this.currentAgendaIndex);
    return currentAgenda?.answers || [];
  }

  // Get counts for current agenda
  get currentAcceptedCount(): number {
    return this.currentAgendaAnswers.filter(a => a.action === 'accept').length;
  }

  get currentRejectedCount(): number {
    return this.currentAgendaAnswers.filter(a => a.action === 'reject').length;
  }

  get currentDeferredCount(): number {
    return this.currentAgendaAnswers.filter(a => a.action === 'defer').length;
  }

  // Select agenda item
  selectAgendaItem(index: number): void {
    this.currentAgendaIndex = index;
  }

  getAgendaStageClass(stage: any): string {
    const value = (stage || '').toString().toLowerCase().replace(/[^a-z0-9]/g, '');
    if (value.includes('unsealingtechnical')) return 'stage-unseal-technical';
    if (value.includes('unsealingcommercial')) return 'stage-unseal-commercial';
    if (value.includes('unsealingcombined')) return 'stage-unseal-combined';
    if (value.includes('evaluationreview')) return 'stage-evaluation-review';
    if (value.includes('awardbid')) return 'stage-award-bid';
    return '';
  }

  // Get user initials for avatar
  getUserInitials(userName: string): string {
    if (!userName) return 'U';
    const parts = userName.trim().split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return userName.substring(0, 2).toUpperCase();
  }

  // Get response badge class
  getResponseBadgeClass(action: string): string {
    switch (action.toLowerCase()) {
      case 'accept':
        return 'badge bg-success';
      case 'reject':
        return 'badge bg-danger';
      case 'defer':
        return 'badge bg-warning';
      default:
        return 'badge bg-secondary';
    }
  }

  // Get response icon
  getResponseIcon(action: string): string {
    switch (action.toLowerCase()) {
      case 'accept':
        return 'fa-check';
      case 'reject':
        return 'fa-times';
      case 'defer':
        return 'fa-clock';
      default:
        return 'fa-question';
    }
  }

  // Get response text
  getResponseText(action: string): string {
    switch (action.toLowerCase()) {
      case 'accept':
        return 'Accepted';
      case 'reject':
        return 'Rejected';
      case 'defer':
        return 'Deferred';
      default:
        return action;
    }
  }

  onClose(): void {
    this.dialogRef.close(true);
  }
}

